import React from 'react';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';

export interface CustomAddButtonProps {
    onClick: () => void;
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
    variant?: 'primary' | 'secondary';
    size?: 'sm' | 'md' | 'lg';
    icon?: string;
    iconPosition?: 'left' | 'right';
}

/**
 * CustomAddButton component for consistent add/new button styling
 * Based on the Add New button from HomePage.tsx
 */
export const CustomAddButton: React.FC<CustomAddButtonProps> = ({
    onClick,
    children,
    className,
    style,
    disabled = false,
    variant = 'primary',
    size = 'md',
    icon = 'fas fa-plus',
    iconPosition = 'left'
}) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);

    // Size-based styling
    const sizeStyles = {
        sm: {
            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
            fontSize: theme.typography.fontSize.sm,
            iconSize: theme.typography.fontSize.xs,
        },
        md: {
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            fontSize: theme.typography.fontSize.md,
            iconSize: theme.typography.fontSize.sm,
        },
        lg: {
            padding: `${theme.spacing.md} ${theme.spacing.lg}`,
            fontSize: theme.typography.fontSize.lg,
            iconSize: theme.typography.fontSize.md,
        },
    };

    const currentSize = sizeStyles[size];

    // Variant-based styling
    const variantStyles = {
        primary: {
            backgroundColor: theme.colors.primary,
            color: theme.colors.text.inverse,
            border: `1px solid ${theme.colors.primary}`,
            borderColor: theme.colors.primary,
            hoverBackgroundColor: theme.colors.primaryHover || theme.colors.primary,
            hoverBorderColor: theme.colors.primaryHover || theme.colors.primary,
        },
        secondary: {
            backgroundColor: 'transparent',
            color: theme.colors.primary,
            border: `1px solid ${theme.colors.primary}`,
            borderColor: theme.colors.primary,
            hoverBackgroundColor: theme.colors.primary + '10',
            hoverBorderColor: theme.colors.primary,
        }
    };

    const currentVariant = variantStyles[variant];

    const buttonStyle: React.CSSProperties = {
        ...themedStyles.button.primary,
        ...currentVariant,
        padding: currentSize.padding,
        fontSize: currentSize.fontSize,
        fontWeight: theme.typography.fontWeight.medium,
        borderRadius: theme.borderRadius.md,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing.xs,
        transition: `all ${theme.transitions.normal}`,
        ...style
    };

    const iconStyle: React.CSSProperties = {
        fontSize: currentSize.iconSize,
        color: 'inherit',
    };

    return (
        <button
            className={className}
            style={buttonStyle}
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={(e) => {
                if (!disabled) {
                    e.currentTarget.style.backgroundColor = currentVariant.hoverBackgroundColor;
                    e.currentTarget.style.borderColor = currentVariant.hoverBorderColor;
                }
            }}
            onMouseLeave={(e) => {
                if (!disabled) {
                    e.currentTarget.style.backgroundColor = currentVariant.backgroundColor;
                    e.currentTarget.style.borderColor = currentVariant.borderColor;
                }
            }}
        >
            {icon && iconPosition === 'left' && (
                <i className={icon} style={iconStyle}></i>
            )}
            {children}
            {icon && iconPosition === 'right' && (
                <i className={icon} style={iconStyle}></i>
            )}
        </button>
    );
};
